/**
 * Enum for specifying date format options when formatting dates.
 */
enum DateFormat 
{
    Date,
    Time,
    DateTime,
    TimeWithSeconds,
    DateTimeWithSeconds
}

/**
 * Enum for specifying predefined date ranges.
 */
enum DateRange 
{
    Yesterday,
    This_Week,
    Last_Week,
    This_Month,
    Last_Month,
    Today
}

/**
 * Interface for serialized data structure.
 */
interface SerializedData 
{
    t: string,
    v: any
}

/**
 * Serializes data into a string representation, handling nested objects and arrays.
 * @param data - The data to serialize.
 * @param level - The recursion level (optional, defaults to 0).
 * @returns A string if at the top level, otherwise a SerializedData object.
 */
const serializer = (data:any, level:number | undefined):string | any =>
{
    let typ = singleton.getType(data);
    let ret = { t: typ } as SerializedData;
    level = level || 0;

    if (typ === "Object")
    {
        let tmp = {} as any;
        for (let p in data)
        {
            tmp[p] = serializer(data[p], level + 1);
        }
        ret.v = tmp;
    }

    else if (typ === "Array") 
    {
        let tmp = [];
        for (let i = 0; i < data.length; i++)
        {
            tmp.push(serializer(data[i], level + 1));
        }
        ret.v = tmp;
    }

    else 
    {
        ret.v = data;
    }

    return (level == 0) ? singleton.toJson(ret) : ret;
}

/**
 * Deserializes a string or object back into its original data type.
 * @param text - The serialized string or object to deserialize.
 * @param level - The recursion level (optional, defaults to 0).
 * @returns The deserialized data or undefined if the format is invalid.
 */
const deserializer = (text:string, level:number | undefined): any =>
{
    let data;
    level = level || 0;

    try 
    {
        data = level < 1 ? singleton.fromJson(text) : text;
    } 
    
    catch (ignore)
    {
        // incorrect format?
        return undefined;
    }

    if (!data || !data.t)
    {
        // incorrect format?
        return undefined;
    }

    // switch the different serialized data types
    switch (data.t)
    {
        case "Date":
        {
            return new Date(data.v);   
        }
        
        case "Number":
        {   
            return Number(data.v);
        }

        case "Null":
        {
            return null;
        }

        case "Undefined":
        {
            return undefined;
        }

        case "Object": 
        {
            let ret = {} as any;
            for (let p in data.v)
            {
                ret[p] = deserializer(data.v[p], level + 1);
            }
            return ret;
        }   

        case "Array":
        {
            let ret = [];
            for (let i = 0; i < data.v.length; i++)
            {
                ret.push(deserializer(data.v[i], level + 1));
            }
            return ret;
        }

        case "Boolean":
        {
            return Boolean(data.v);
        }

        case "String":
        default:
        {
            return data.v + "";   
        }
    }
}

/**
 * A utility class providing helper methods for date formatting, serialization, 
 * and URL parameter handling.
 */
class Helper
{
    // DateFormat enum
    static readonly DateFormat = DateFormat;

    // DateRange enum
    static readonly DateRange = DateRange;

    // for working with JS modules ...
    DateFormat_Date = 0;
    DateFormat_Time = 1;
    DateFormat_DateTime = 2;
    DateFormat_TimeWithSeconds = 3;
    DateFormat_DateTimeWithSeconds = 4;

    /**
     * Formats a JavaScript Date object to a string compatible with HTML5 input elements.
     * @param date - The Date object to format.
     * @param format - The desired format (optional, defaults to DateFormat.Date).
     * @returns A formatted date string.
     */
    dateToInput(date:Date | string | null, format: DateFormat | undefined): string
    {
        if (!date)
        {
            return "";
        }

        if (typeof date === "string")
        {
            // Only append Z for date-only strings to prevent timezone shift
            // Don't append Z if string already has time component (T or space separator)
            if (date.indexOf("T") === -1 && date.indexOf(" ") === -1 && date.indexOf("Z") === -1)
            {
                date = new Date(date + "Z");
            }
            else
            {
                // Has time component - treat as local time
                date = new Date(date);
            }
        }

        let dateString:string = date.getFullYear() + "-" + 
            (("0" + (date.getMonth() + 1)).slice(-2)) + "-" +
            (("0" + (date.getDate())).slice(-2)) + " " + 
            (("0" + (date.getHours())).slice(-2)) + ":" +
            (("0" + (date.getMinutes())).slice(-2)) + ":" + 
            (("0" + (date.getSeconds())).slice(-2));

        format = format || DateFormat.Date;

        switch (format)
        {
            case DateFormat.Date:
            {
                dateString = dateString.substring(0, dateString.length - 3);
                dateString = dateString.split(" ")[0];
                break;
            }
                
            case DateFormat.Time:
            {
                dateString = dateString.substring(0, dateString.length - 3);
                dateString = dateString.split(" ")[1];
                break;
            }

            case DateFormat.DateTime:
            {
                dateString = dateString.substring(0, dateString.length - 3);
                dateString = dateString.replace(" ", "T");
                break;
            }

            case DateFormat.TimeWithSeconds:
            {
                dateString = dateString.split(" ")[1];
                break;
            }

            case DateFormat.DateTimeWithSeconds:
            {
                // time is already formatted correctly
                break;
            }
        }

        return dateString;
    }

    dateFromString(dateString:string | null):Date | null
    {
        if (!dateString)
        {
            return null;
        }

        console.log(`[dateFromString] Input: "${dateString}"`);

        if (-1 < dateString.indexOf("GMT"))
        {
            const result = new Date(dateString);
            console.log(`[dateFromString] GMT detected, returning:`, result);
            return result;
        }

        // we want the string to always be in local time ...
        // when it comes from the server it will have a "T" in it ...
        
        let suffix = "";

        // If the string is just a date (YYYY-MM-DD) without time, add time component
        // This ensures "2025-11-06" becomes "2025-11-06 00:00:00" (local midnight)
        // instead of being interpreted as UTC midnight by new Date()
        if (dateString.indexOf("Z") < 0 && dateString.indexOf("T") < 0 && dateString.indexOf(" ") < 0)
        {
            // Just a date string, add time component for local interpretation
            dateString = dateString + " 00:00:00";
            console.log(`[dateFromString] Date-only detected, modified to: "${dateString}"`);
        }

        if (dateString.indexOf("Z") < 0)
        {
            if (-1 < dateString.indexOf("T"))
            {
                dateString = dateString.split("T")[0] + " " + 
                (dateString.split("T")[1] || "00:00:00");
                console.log(`[dateFromString] No Z, has T, modified to: "${dateString}"`);
                // Don't append Z - treat as local time
            }

            else
            {
                dateString = dateString.split(" ")[0] + " " + 
                    (dateString.split(" ")[1] || "00:00:00");   
                console.log(`[dateFromString] No Z, no T, modified to: "${dateString}"`);
            }
        }
        else
        {
            console.log(`[dateFromString] Has Z, using as-is: "${dateString}"`);
        }

        const result = new Date(dateString + suffix);
        console.log(`[dateFromString] Returning Date:`, result, `for input: "${dateString}"`);
        
        return result;
    }

    /**
     * Returns the start and end dates for a specified date range as formatted strings.
     * @param range - The date range (optional, defaults to DateRange.Today).
     * @returns An array of two strings: [startDate, endDate].
     */
    getDateRange(range: DateRange | undefined): Array<string>
    {
        let tmp = new Date();
        let str = "", end = "";

        switch (range)
        {
            case DateRange.Yesterday:
            {
                tmp.setDate(tmp.getDate() - 1);
                str = this.dateToInput(tmp, DateFormat.Date);
                end = this.dateToInput(tmp, DateFormat.Date);
                break;
            }

            case DateRange.This_Week:
            {
                tmp.setDate(tmp.getDate() - tmp.getDay());
                str = this.dateToInput(tmp, DateFormat.Date);
                tmp.setDate(tmp.getDate() + 6);
                end = this.dateToInput(tmp, DateFormat.Date);
                break;
            }

            case DateRange.Last_Week:
            {
                tmp.setDate((tmp.getDate() - tmp.getDay()) - 7);
                str = this.dateToInput(tmp, DateFormat.Date);
                tmp.setDate(tmp.getDate() + 6);
                end = this.dateToInput(tmp, DateFormat.Date);
                break;
            }

            case DateRange.This_Month:
            {
                tmp.setDate(1);
                str = this.dateToInput(tmp, DateFormat.Date);
                tmp.setMonth(tmp.getMonth() + 1);
                tmp.setDate(tmp.getDate() - 1);
                end = this.dateToInput(tmp, DateFormat.Date);
                break;
            }

            case DateRange.Last_Month:
            {
                tmp.setDate(1);
                tmp.setMonth(tmp.getMonth() - 1);
                str = this.dateToInput(tmp, DateFormat.Date);
                tmp.setMonth(tmp.getMonth() + 1);
                tmp.setDate(tmp.getDate() - 1);
                end = this.dateToInput(tmp, DateFormat.Date);
                break;
            }

            case DateRange.Today:
            default:
            {
                str = this.dateToInput(tmp, DateFormat.Date);
                end = this.dateToInput(tmp, DateFormat.Date);
                break;
            }
        }

        return [str, end];
    }

    isDate(str:string)
    {
        return !isNaN(Date.parse(str));
    }

    /**
     * Creates a deep copy of an object by serializing and deserializing it.
     * @param obj - The object to copy.
     * @returns A deep copy of the object.
     */
    copy(obj:any):any
    {
        console.debug("copy original:", obj);

        let tmp1:any = this.serialize(obj);

        console.debug("serialize", tmp1);

        let tmp2:any = this.deserialize(tmp1);

        console.debug("deserialize", tmp2);
        
        return tmp2;
    }

    prep(obj:any):any 
    {
        let tmp:any = {};

        console.debug("pre prep: ", obj);
        
        for (let p in obj)
        {
            let key:string = p;
            let val:any = obj[p];

            // add all getters to object as properties ...
            if (p.indexOf("_") === 0)
            {
                // store the getter value instead of the private value ...
                key = p.substring(1);
                val = obj[key];

                if (this.isDate(val))
                {
                    console.debug("preDateFromString: ", val);
                    val = this.dateFromString(val);
                    console.debug("postDateFromString: ", val);
                }

                // "null" out as default ...
                if (val === "" || val === null || val === undefined)
                {
                    val = null;
                }

                else if (this.getType(val) === "Object" || this.getType(val) === "Array")
                {
                    // don't need nested objects ...
                    continue;
                }

                tmp[key] = val;
                continue;
            }

            if (val === "" || val === null || val === undefined)
            {
                val = null;
            }

            else if (this.getType(val) === "Object" || this.getType(val) === "Array")
            {
                // don't need nested objects ...
                continue;
            }

            tmp[p] = val;
        }

        console.debug("post prep / pre copy: ", tmp);
        
        let copy = this.copy(tmp); 
        
        console.debug("post copy: ", copy);
        
        return copy;
    }
    
    /**
     * Converts an object to a JSON string.
     * @param data - The data to convert.
     * @returns A JSON string representation of the data.
     */
    toJson(data:any):string
    {
        return JSON.stringify(data);
    }

    /**
     * Parses a JSON string into an object.
     * @param json - The JSON string to parse.
     * @returns The parsed object.
     */
    fromJson(json:string):any
    {
        return JSON.parse(json);
    }

    /**
     * Converts URL parameters to an object.
     * @param params - The URL parameter string (e.g., "key1=value1&key2=value2").
     * @returns An object representing the parameters.
     */
    fromUrlParams(params:string)
    {
        // empty object if no params provided
        if (!params) 
        {
            return {};
        }

        // This doesn't work for arrays... todo...
        let json = '{"' + decodeURI(params.replace(/&/g, "\",\"").replace(/=/g,"\":\"")) + '"}';
        return this.fromJson(json);
    }

    /**
     * Converts an object to a URL parameter string.
     * @param obj - The object to convert.
     * @returns A URL parameter string (e.g., "key1=value1&key2=value2").
     */
    toUrlParams(obj:any):string
    {
        let str = "";
        for (let p in obj)
        {
            str += p + "=" + obj[p] + "&";
        }
        str = str.substring(0, str.length - 1);
        return str;
    }

    /**
     * Serializes data into a string.
     * @param data - The data to serialize.
     * @returns A serialized string.
     */
    serialize(data:any):string
    {
        return serializer(data, 0) as string;
    }

    /**
     * Deserializes a string into its original data.
     * @param text - The serialized string.
     * @returns The deserialized data or undefined if invalid.
     */
    deserialize(text:string):any
    {
        return deserializer(text, 0);
    }

    /**
     * Gets the type of a value as a string.
     * @param param - The value to check.
     * @returns The type name (e.g., "String", "Object", "Date").
     */
    getType(param:any):string
    {
        let str = Object.prototype.toString.apply(param).substring(8);
        return str.substring(0, str.length - 1);
    }
}

const singleton = new Helper();

export default singleton;

export { DateFormat, DateRange };